//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import ArrayStore from 'devextreme/data/array_store'

//#endregion


import { ConvertHelper, LocalizationHelper, LogHelper, TypeHelper, ReferenceTypeComponent } from '../../helpers'
import { Enums } from "../../models/common";
import { OpData, OpUnit } from '../../models/core'
import { IOpObject, IOpData, IReferenceType } from '../../models/util/interfaces'
import { ColumnModel } from "../../modules/api/api.models";
import { GridTypes } from "../../modules/common/grid/grid.controller";
import { TreeListOptions } from "../../modules/common/tree-list/tree-list.controller";
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

// #region Consts

export const EXTENDED_PARAMETERS_SERVICE_NAME: string = 'ExtendedParametersService'
export const EXTENDED_PARAMETERS_SERVICE_PROVIDER_NAME: string = 'ExtendedParametersServiceProvider'

// #endregion

// #region Public types

export type ExtendedParametersDataSourceOptions = {
    opObject: IOpObject
    allowedDataTypes: number[]
    unitProvider: (data: IOpData) => OpUnit
}

export type ExtendedParametersOptions = {
    treeName: string,
    showLegend?: boolean
}

// #endregion

export class ExtendedParametersService {
    // #region Consts

    public static get TreeKeyFieldName() { return 'idTreeItem' }
    public static get TreeParentFieldName() { return 'idParentTreeItem' }

    public static get DataRowTypeName() { return 'data' }
    public static get ValueDataFieldName() { return 'value' }

    public static get LOG_PREFIX(): string { return '[SIMAX.Service.ExtendedParameters]' }

    // #endregion

    // #region Ctor

    constructor(
        private userSession: UserSessionService
    ) {
    }

    // #endregion

    // #region Public Methods

    public onExtendedParameterCellPrepared(options: any): void {
        if (options != null
            && options.rowType == ExtendedParametersService.DataRowTypeName
            && options.column.dataField == ExtendedParametersService.ValueDataFieldName
            && options.data != null
        ) {
            try {
                if (options.data instanceof OpData) {
                    const data: OpData = options.data

                    if (data.dataType == null) {
                        data.dataType = this.userSession
                            .getDataProvider()
                            .DataType
                            .getDataTypeLocal(data.idDataType)
                    }

                    if (data.value != null) {
                        if (data.dataType.idReferenceType != null) {
                            ReferenceTypeComponent.getReferenceObject(
                                this.userSession.getDataProvider(),
                                data.dataType.idReferenceType,
                                data.value
                            )
                            .then((referenceObject: IReferenceType) => options.cellElement.html(referenceObject == null ? null : referenceObject.toString()))

                        } else {
                            options.cellElement.html(ConvertHelper.prepareDataValue(data.value, data.dataType))
                        }
                    }
                } else if (options.data.rows != null) {
                    $.when(options.data.rows.map((row: OpData) => {

                        if (row != null) {
                            if (row.value != null) {
                                if (row.dataType.idReferenceType != null) {
                                    return ReferenceTypeComponent.getReferenceObject(
                                        this.userSession.getDataProvider(),
                                        row.dataType.idReferenceType,
                                        row.value
                                    )
                                        .then((referenceObject: IReferenceType) => referenceObject == null ? null : referenceObject.toString())

                                } else {
                                    return ConvertHelper.prepareDataValue(row.value, row.dataType)
                                }
                            }
                        }

                        return null
                    })).then((values: any[]) => {
                        if (_.size(values) > 0) {
                            options.cellElement.html(values.join(';'))
                        } else {
                            options.cellElement.html(TypeHelper.emptyString)
                        }
                    })
                }
            } catch (ex) {
                LogHelper.error(ExtendedParametersService.LOG_PREFIX, ex)
                options.cellElement.html(null)
            }
        }
    }

    // #endregion

    // #region Columns

    public getExtededParametersGridColumns(): ColumnModel[] {
        const descriptionColumn: ColumnModel = new ColumnModel()
        descriptionColumn.idName = 'dataType'
        descriptionColumn.name = 'DataType'
        descriptionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)
        descriptionColumn.type = Enums.DataTypeClass.Text

        const indexColumn: ColumnModel = new ColumnModel()
        indexColumn.idName = 'indexNbr'
        indexColumn.name = 'Index'
        indexColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index)
        indexColumn.type = Enums.DataTypeClass.Text

        const valueColumn: ColumnModel = new ColumnModel()
        valueColumn.idName = 'value'
        valueColumn.name = 'Value'
        valueColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value)
        valueColumn.type = Enums.DataTypeClass.Text

        const unitColumn: ColumnModel = new ColumnModel()
        unitColumn.idName = 'unit'
        unitColumn.name = 'Unit'
        unitColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Unit)
        unitColumn.type = Enums.DataTypeClass.Text

        return [descriptionColumn, indexColumn, valueColumn, unitColumn]
    }

    // #endregion

    // #region Options

    public getExtendedParametersOptions(options: ExtendedParametersOptions): TreeListOptions {
        const treeListOptions: TreeListOptions = {
            name: options.treeName,
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            buttonsOptions: {
                enabled: options.showLegend,
            },

            addCustomFilters: true,
            columnsDictionary: this.getExtededParametersGridColumns(),
            dataSource: null,
            showDatatypeLegend: options.showLegend,
            localStorageVarForSavedEntity: null,

            keyExpr: ExtendedParametersService.TreeKeyFieldName,
            parentIdExpr: ExtendedParametersService.TreeParentFieldName
        }

        return treeListOptions
    }

    public createDataSourceForExtendedParameters(options: ExtendedParametersDataSourceOptions): any {
        const items: any[] = []

        if (options != null
            && options.opObject != null
            && options.opObject.dataList != null
            && options.opObject.dataList.length > 0
        ) {
            const filteredData: IOpData[] = options.opObject.dataList.toArray().filter((dItem: IOpData) => options.allowedDataTypes.some((idDataType: number) => dItem.idDataType === idDataType))

            let treeItemId: number = +new Date()

            for (let dItem of filteredData) {
                if (dItem.dataType == null) {
                    dItem.dataType = this.userSession.getDataProvider().DataType.getDataTypeLocal(dItem.idDataType)
                }

                const treeItem: any = dItem
                treeItem[ExtendedParametersService.TreeKeyFieldName] = treeItemId++
                treeItem[ExtendedParametersService.TreeParentFieldName] = 0
                items.push(treeItem)
            }

            const grouping: _.Dictionary<IOpData[]> = _.groupBy(filteredData, 'idDataType')

            if (grouping != null) {
                for (let groupKey in grouping) {
                    const groupedData: IOpData[] = grouping[groupKey]
                    if (groupedData.length > 1) {

                        const sourceItem: IOpData = groupedData[0]
                        const unit: OpUnit = options.unitProvider(sourceItem)

                        const groupingRowItem: any = {
                            dataType: sourceItem.dataType,                             // dataType column
                            indexNbr: groupedData.map(row => row.indexNBR).join(''),   // indexNbr column
                            unit: unit,                                                // unit column
                            rows: groupedData,                                         // child rows
                            typeId: sourceItem.dataType.idDataTypeClass,               // ???
                        }

                        groupingRowItem[ExtendedParametersService.TreeKeyFieldName] = sourceItem.idDataType
                        groupingRowItem[ExtendedParametersService.TreeParentFieldName] = 0

                        items.push(groupingRowItem)

                        groupedData.forEach((row: OpData) => {
                            row[ExtendedParametersService.TreeParentFieldName] = sourceItem.idDataType
                        })
                    }
                }
            }
        }

        return new ArrayStore({
            key: ExtendedParametersService.TreeKeyFieldName,
            data: items
        })
    }

    // #endregion

    // #region Private Methods

    // #endregion
}

export class ExtendedParametersServiceProvider
    implements ng.IServiceProvider {
    // #region IServiceProvider Implementation 

    public $get = [
        USER_SESSION_SERVICE_NAME,
        function (
            userSession: UserSessionService
        ) {
            return new ExtendedParametersService(userSession)
        }
    ]

    // #endregion
} 
 